import { inject, Injectable } from '@angular/core';
import { NewCustomRouteService } from 'src/app/pages/new-custom-route/new-custom-route.service';
import { DataChatService } from './dataChat.service';
import { Router } from '@angular/router';
import { validate as uuidValidate, version as uuidVersion } from 'uuid';
import { RestService } from 'src/app/services';
import { extractUrls as extractUrlsFromText } from './chat-actions/helpers/url-extract.util';
import { EmbedContextService } from 'src/app/services/embed-context.service';
import { ToolsService } from 'src/app/services/tools.service';
import { DebugService } from 'src/app/services/debug.service';

@Injectable({ providedIn: 'root' })

export class AssistantToolsHandlerService {
  private rest = inject(RestService);
  private router = inject(Router);
  private dataChat = inject(DataChatService);
  private customRouteService = inject(NewCustomRouteService);
  private embedContext: EmbedContextService = inject(EmbedContextService);
  private tools: ToolsService = inject(ToolsService);
  private debug = inject(DebugService);

// POI Buffer per ID
  private partialActionBuffers = new Map<string, string>();

  /**
   * URLs de acciones (SHOW_POI/POIS) que llegaron ANTES de que existiera el mensaje del
   * asistente. El orden de los eventos SSE no está garantizado: v3 emite tokens (que crean
   * el mensaje) antes del action_tag, pero dev emite action_tag → final sin tokens, así
   * que la acción llega cuando messages() aún no tiene la fila. Se aparcan aquí y las
   * fusiona handleTextUrls al completar (es quien pone `finished`, el gate del preview).
   */
  private pendingUrls = new Map<string, string[]>();

  /**
   * Descarta el tag [ACTION:…] a medias que pudiera haber dejado el último token de un
   * mensaje. Lo llama la reconciliación de `final`: su texto ya es completo y limpio, y
   * processAssistantTools antepone el buffer parcial al texto que recibe — sin esto, un
   * fragmento huérfano acabaría pegado delante del texto definitivo.
   */
  public resetPartialActionBuffer(id: string): void {
    this.partialActionBuffers.delete(id);
  }

  // HRS-3045: la reconciliación de `final` re-parsea el texto completo, y con él los
  // tags [ACTION:SHOW_POI:...] ya procesados durante el streaming. Sin este registro,
  // la re-pasada repetía la navegación/modal del POI. Clave: mensaje + URL de tarjeta.
  private processedActionKeys = new Set<string>();

  public processAssistantTools(id: string, text: string): string {
    let buffer = (this.partialActionBuffers.get(id) || "") + text;
    let outputText = "";

    // 1. Process Complete Actions - WEATHER PRIMERO (tiene estructura más compleja)
    buffer = this.extractAndHandleActionTag(id, buffer, '[ACTION:SHOW_WEATHER:', (jsonData) => {
      this.handleWeatherAction(id, jsonData);
    });

    // 2. Luego POI
    buffer = this.extractAndHandleActionTag(id, buffer, '[ACTION:SHOW_POI:', (jsonData) => {
      this.handlePoiAction({ ...jsonData, completion_id: id});
    });

        // 2. Luego POI
    buffer = this.extractAndHandleActionTag(id, buffer, '[ACTION:SHOW_POIS:', (jsonData) => {
      this.handlePoisAction({ ...jsonData, completion_id: id});
    });

    // 3. Fallback para WEB (cuando no hay POI oficial)
    // buffer = this.extractAndHandleActionTag(id, buffer, '[ACTION:SHOW_WEB:', (jsonData) => {
    //   console.log('🚀 [TextAnim] WEB Action JSON data:', jsonData);
    //   this.handleExternalUrlAction(jsonData);
    // });

    // 3. Check for partials at end
    const lastOpen = buffer.lastIndexOf('[');
    if (lastOpen !== -1) {
      const potentialTag = buffer.substring(lastOpen);
      // Si parece que empieza una acción, esperamos al siguiente chunk para cerrarla
      if (potentialTag.startsWith("[ACTION:")) {
        outputText = buffer.substring(0, lastOpen);
        this.partialActionBuffers.set(id, potentialTag);
        // Limpieza final de residuos
        return this.cleanActionResidues(outputText);
      }
    }

    // No hay tag parcial, flush
    this.partialActionBuffers.set(id, "");
    // Limpieza final de residuos
    return this.cleanActionResidues(buffer);
  }

  private extractAndHandleActionTag(id: string, buffer: string, prefix: string, handler: (jsonData: any) => void): string {
    let startIdx = buffer.indexOf(prefix);
    while (startIdx !== -1) {
      const jsonStart = startIdx + prefix.length;
      if (jsonStart >= buffer.length) 
        break;

      const firstChar = buffer[jsonStart];
      if (firstChar !== '{' && firstChar !== '[') {
        console.warn(`⚠️ [TOOLS] JSON inválido tras ${prefix}: no empieza por { ni [`);
        startIdx = buffer.indexOf(prefix, startIdx + 1);
        continue;
      }

      let stack: string[] = [firstChar];
      let inString = false;
      let escapeNext = false;
      let jsonEnd = -1;

      for (let i = jsonStart + 1; i < buffer.length; i++) {
        const char = buffer[i];

        if (escapeNext) {
          escapeNext = false;
          continue;
        }

        if (char === '\\' && inString) {
          escapeNext = true;
          continue;
        }

        if (char === '"') {
          inString = !inString;
          continue;
        }

        if (!inString) {
          if (char === '{' || char === '[') {
            stack.push(char);
          } else if (char === '}') {
            if (stack[stack.length - 1] === '{') {
              stack.pop();
            }
          } else if (char === ']') {
            if (stack[stack.length - 1] === '[') {
              stack.pop();
            }
          }

          if (stack.length === 0) {
            jsonEnd = i + 1;
            break;
          }
        }
      }

      if (jsonEnd !== -1) {
        const jsonString = buffer.substring(jsonStart, jsonEnd);

        try {
          const jsonData = JSON.parse(jsonString);
          handler(jsonData);

          buffer = buffer.substring(0, startIdx) + buffer.substring(jsonEnd);
          startIdx = buffer.indexOf(prefix);
        } catch (e) {
          console.error(`❌ [TOOLS] Error parsing JSON para ${prefix}:`, e);
          startIdx = buffer.indexOf(prefix, startIdx + 1);
        }
      } else {
        console.log(`⏳ [TOOLS] Tag incompleto, esperando más datos...`);
        break;
      }
    }

    return buffer;
  }

  /** Limpia cualquier residuo de action tags que haya quedado */
  private cleanActionResidues(text: string): string {
    if (!text) return text;

    // Patrones de residuos comunes de action tags mal parseados
    // IMPORTANTE: No eliminar espacios normales, solo caracteres de action tags
    let cleaned = text
      .replace(/^\}?\]?(?=\S)/g, '')         // }] al principio solo si hay texto después
      .replace(/(?<=\S)\}?\]?$/g, '')        // }] al final solo si hay texto antes
      .replace(/\[ACTION:[^\]]*$/g, '')      // Action tags incompletos al final
      .replace(/\}\]\s*\[ACTION:/g, '[ACTION:')  // }] entre action tags

    // Eliminar líneas que solo son basura de action tags
    if (/^[\}\]\[\s]+$/.test(cleaned)) {
      return '';
    }

    return cleaned;
  }

  private handleWeatherAction(id: string, weatherData: any, retryCount: number = 0) {
    console.log('🌤️ [TOOLS] handleWeatherAction llamado con:', { id, weatherData, retryCount });

    const messages = this.dataChat.messages();
    let index = messages.findIndex((m: any) => m.id === id);

    // Si no encontramos el mensaje por ID, buscar el último mensaje de assistant
    if (index === -1) {
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === 'assistant') {
          index = i;
          break;
        }
      }
    }

    if (index !== -1) {
      this.dataChat.messages.update((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], weatherData };
        return updated;
      });
    } else if (retryCount < 5) {
      // El mensaje aún no existe, reintentar después de un delay
      setTimeout(() => {
        this.handleWeatherAction(id, weatherData, retryCount + 1);
      }, 300);
    } else {
      console.log('🌤️ [TOOLS] ❌ No se encontró ningún mensaje después de 5 reintentos');
    }
  }

  /** Índice del mensaje NO-usuario con ese id (o -1). */
  private findAssistantIndex(messages: any[], id: string): number {
    // HRS-3045 (port): m.rol era un typo silencioso (el campo real es role) que dejaba el guard muerto.
    return messages.findIndex((m: any) => m.id === id && m.role !== 'user');
  }

  /**
   * Construye la URL pública absoluta de un recurso relativa al despliegue actual
   * (origin + <base href> vía document.baseURI), en vez de hardcodear el host/subdominio.
   * Así funciona tanto en los portales en raíz (base "/", p.ej. <sub>.ciceroneweb.com)
   * como en los self-hosted con prefijo (base "/front/", p.ej. miruta.visitmadrid.es/front).
   * El poi_url llega con "/" inicial; se quita para que resuelva relativo a la base.
   */
  private buildResourceUrl(poiUrl: string): string {
    if (!poiUrl)
      return poiUrl;

    // Si el backend la envía ABSOLUTA (con protocolo), se RESPETA tal cual
    // (el backend decidió el destino a propósito).
    if (/^https?:\/\//i.test(poiUrl))
      return poiUrl;

    try {
      // RELATIVA: se resuelve respetando el base-href del despliegue (conserva subcarpeta
      // tipo /front/), pero SIEMPRE sobre el ORIGEN donde corre la app
      // (window.location.origin), NO el host horneado en <base href>. Así los enlaces
      // usan el entorno desplegado (p.ej. visit.val.comunidad.madrid) y no el de build
      // (miruta.visitmadrid.es).
      const resolved = new URL(poiUrl.replace(/^\//, ''), document.baseURI);
      return new URL(resolved.pathname + resolved.search + resolved.hash, window.location.origin).href;
    } catch {
      return poiUrl;
    }
  }

  /**
   * URL de la tarjeta. En el embed, la web pública del recurso; si no la tiene (los
   * eventos del CMS de Madrid no la traen), su ficha en el front, para no perder la
   * tarjeta que el asistente ya manda con nombre e imagen (HRS-3865).
   */
  private cardUrlOf(poi: any): string | undefined {
    return (this.embedContext.isEmbed() && poi?.public_url) || poi?.poi_url;
  }

  private async handlePoiAction(poiData: any) {
    console.log('🚀 [TOOLS] Handling POI Action. Current State:', poiData);
    const poiUrl = this.cardUrlOf(poiData);
    if (!poiUrl)
      return;

    const cardUrl = this.buildResourceUrl(poiUrl);

    // Ya procesada en el streaming: la re-pasada de `final` no repite navegación/modal
    // ni re-siembra la tarjeta (HRS-3045).
    const actionKey = `poi:${poiData.completion_id}:${cardUrl}`;
    if (this.processedActionKeys.has(actionKey))
      return;
    this.processedActionKeys.add(actionKey);

    this.seedPoiCard(poiData, cardUrl);
    this.handleInternalUrls(poiData.completion_id, [cardUrl]);

    // Single SHOW_POI — comportamiento por superficie (la tarjeta/preview ya se añadió arriba):
    //  - Script (widget cargado por cicerone-chat.js, isEmbed): SOLO la tarjeta, nada más.
    //  - ciceroneweb (front, no script):
    //      · en planner (modo planner ?planner=true o ruta /custom-route): abrir modal.
    //      · sin planner: navegar a la ficha (routing).
    if (this.embedContext.isEmbed())
      return;

    if (this.tools.isPlannerMode() || this.isInPlannerContext()) {
      const poiId = Number(poiData.poi_id);
      if (poiId)
        this.customRouteService.modalItemInfo({ id: poiId, type: poiData.poi_type });
      return;
    }

    this.router.navigateByUrl(poiUrl);
  }

  /** Siembra los metadatos de tarjeta (nombre + imagen) que ya trae la acción SHOW_POI/POIS,
   *  para que <link-preview> pinte con contenido real sin depender del /api/link-preview
   *  (que en el self-hosted scrapea y devuelve título genérico). Clave = URL de la tarjeta. */
  private seedPoiCard(poi: any, url: string): void {
    if (!url || !poi || (!poi.poi_name && !poi.image)) return;
    const image = poi.image
      ? (/^https?:\/\//.test(poi.image) ? poi.image : `${this.rest.apiUrlBase}${poi.image}`)
      : undefined;
    this.dataChat.linkPreviewMeta.set(url, {
      title: poi.poi_name,
      image,
      site_name: this.rest.subDomain,
    });
  }

  private async handlePoisAction(poiData: any) {
    console.log('🚀 [TOOLS] Handling MULTI POIS Action. Current State:', poiData);

    const urls: string[] = [];
    for (const e of (Object.values(poiData) as any[])) {
      const url = this.cardUrlOf(e);
      if (!url) continue;
      const u = this.buildResourceUrl(url);
      this.seedPoiCard(e, u);
      urls.push(u);
    }

    // Plural SHOW_POIS — SOLO previews (tarjetas): nunca modal ni routing, en ninguna
    // superficie (script, planner o site). No se abre sidebar ni se navega.
    if (urls.length > 0)
      this.handleInternalUrls(poiData.completion_id, urls);
  }

  /** Detecta si estamos en la vista del planner/custom-route */
  private isInPlannerContext(): boolean {
    return this.router.url.includes('custom-route');
  }

  private async handleExternalUrlAction(webData: any) {
    const url = webData.url;
    if (!url) 
      return;

    // Transformar a formato compatible con poiList
    const virtualPoi = {
      poi_id: `web_${btoa(url).substring(0, 16)}`, // ID estable para la URL
      poi_name: webData.title || 'Sitio Web',
      poi_url: url,
      image: null,
      is_fallback_web: true
    };

    this.dataChat.poiList.update(list => {
      const existsIndex = list.findIndex((p: any) => p.poi_url === url);
      if (existsIndex === -1) {
        const newList = [...list, virtualPoi];
        this.dataChat.activePoiIndex.set(newList.length - 1);
        this.dataChat.isPoiSidebarOpen.set(true);
        return newList;
      } else {
        this.dataChat.activePoiIndex.set(existsIndex);
        this.dataChat.isPoiSidebarOpen.set(true);
        return list;
      }
    });
  }

  /** Id base sin el sufijo de proactivo (mismo criterio que webSocket.service). */
  private baseId(id: string): string {
    return id?.split('_tip')?.[0];
  }

  /**
   * HRS-3045: los ids de proactivos ({uuid}_tip) son ids de mensaje legítimos, así que
   * la validación se hace sobre el id BASE. Los rechazos se loguean para tener
   * visibilidad de ids no-v4 reales (texto/voz/imagen) sin bloquear en silencio.
   */
  private isTrackableMessageId(id: string): boolean {
    const base = this.baseId(id);
    const isValidV4 = !!base && uuidValidate(base) && uuidVersion(base) === 4;
    if (!isValidV4)
      this.debug.warn(id, '[TOOLS] id de mensaje no UUIDv4: se omite la asociación de URLs');
    return isValidV4;
  }

  /** Merge con dedupe: la doble pasada streaming + final no debe duplicar tarjetas. */
  private mergeUrls(previous: string[] | undefined, incoming: string[]): string[] {
    return [...new Set([...(previous ?? []), ...incoming])];
  }

  public handleInternalUrls(id: string, urls: string[]){
    if (!this.isTrackableMessageId(id))
      return;

    // La acción llegó antes que el texto (dev: action_tag → final, sin tokens): la fila del
    // asistente aún no existe. Aparcar; handleTextUrls las fusiona al completar el mensaje.
    if (this.findAssistantIndex(this.dataChat.messages(), id) === -1) {
      this.pendingUrls.set(id, this.mergeUrls(this.pendingUrls.get(id), urls));
      this.debug.debug({ id, urls }, '[TOOLS] mensaje aún no existe: URLs aparcadas hasta completar');
      return;
    }

    this.dataChat.messages.update((prev) => {
      const index = this.findAssistantIndex(prev, id);
      if (index === -1)
        return prev;

      const updated = [...prev];
      updated[index] = { ...updated[index], urls: this.mergeUrls(updated[index]?.urls, urls) };
      return updated;
    });
  }

  public handleTextUrls(id: any, text: any){
    if (!this.isTrackableMessageId(id))
      return;

    // Sin fila aún no hay nada que asociar: las pendientes se conservan para el próximo intento.
    if (this.findAssistantIndex(this.dataChat.messages(), id) === -1)
      return;

    // Primero las URLs de acciones aparcadas (mismo orden que cuando llegan con la fila ya creada:
    // acción durante el stream, texto al completar), luego las del texto. mergeUrls deduplica.
    const pending = this.pendingUrls.get(id) ?? [];
    this.pendingUrls.delete(id);
    const urls = this.extractUrls(text);

    this.dataChat.messages.update((prev) => {
      const index = this.findAssistantIndex(prev, id);
      if (index === -1)
        return prev;

      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        urls: this.mergeUrls(this.mergeUrls(updated[index]?.urls, pending), urls),
        finished: true,
      };
      return updated;
    });
  }

  // HRS-2498 (TNI): extracción afinada (incl. dominios pelados) con corpus anti-falsos-positivos.
  // Lógica pura y testeada en chat-actions/helpers/url-extract.util(.spec).ts.
  extractUrls(content: string): string[] {
    const origin = typeof window !== 'undefined' ? window.location.origin : undefined;
    return extractUrlsFromText(content, origin);
  }

}